import React from "react";
import { ScrollArea, Divider, Group, Button, Box, Text } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import FilterDropdownSortSelector from "./FilterDropdownSortSelector";
import FilterDropdownFieldSelector from "./FilterDropdownFieldSelector";
import FilterDropdownValueSelector from "./FilterDropdownValueSelector";

export default function FilterSelectionPanel({
  section,
  filters,
  filtersSelected,
  panelFilters,
  isSortVisible,
  sortSelected,
  tagsList,
  tagsSelected,
  paymentFilters,
  optionCounts,
  topLocations,
  onSelectFilter,
  onUpdateFilter,
  onUpdateSort,
  onPressCreateFilter,
  onSelectTag
}) {
  if (!Array.isArray(filters)) return null;

  const selectedIds = new Set(filtersSelected.map(f => f.id));
  const mainFilters = filters.filter(f => (!selectedIds.has(f.id) && !["image", "file"].includes(f.format)));
  const bundles = Array.isArray(paymentFilters?.bundles) ? paymentFilters.bundles : [];
  const coupons = Array.isArray(paymentFilters?.coupons) ? paymentFilters.coupons : [];
  const survey_options = Array.isArray(paymentFilters?.survey_options) ? paymentFilters.survey_options : [];
  const paymentCounts = Array.isArray(paymentFilters?.usage_counts) ? paymentFilters.usage_counts : [];

  const allCounts = Array.isArray(optionCounts) ? [...paymentCounts, ...optionCounts] : paymentCounts;
  
  const aditionalFilters = [
    bundles.length > 0 ? {
      id: "bundle",
      name: "Bundle",
      format: "select",
      options: {
        values: bundles.map((b, i) => ({
          value: b.id,
          name: b.name,
          sort_order: i
        }))
      }
    } : null,
    coupons.length > 0 ? {
      id: "coupon",
      name: "Coupon",
      format: "select",
      options: {
        values: coupons.map((c, i) => ({
          value: c.id,
          name: c.code,
          sort_order: i
        }))
      }
    } : null,
    paymentFilters?.payment_survey ? {
      id: "payment_survey",
      name: "Flexible payment option",
      format: "select",
      options: {
        values: survey_options.map((c, i) => ({
          value: c.id,
          name: c.content,
          sort_order: i
        }))
      }
    } : null
  ].filter(f => (f && !selectedIds.has(f.id)));

  const managerAdditionalSort = section === "members-settings" ? [
    {
      id: "member_name",
      name: "Name",
      format: "text"
    },
    {
      id: "member_email",
      name: "Email",
      format: "text"
    },
    {
      id: "member_date_joined",
      name: "Date joined",
      format: "text"
    }
  ] : [];

  const availableFilters = [...mainFilters, ...aditionalFilters];

  const onUpdate = (filter, option_value, condition) => {
    if (typeof onUpdateFilter === "function") {
      let newOptions = [];
      if (Array.isArray(option_value)) {
        newOptions = option_value;
      } else if (typeof option_value === "string" && option_value.length) {
        newOptions = [option_value]
      }
      onUpdateFilter({...filter, condition, selectedOptions: newOptions});
    }
  };

  return (
    <ScrollArea
      type="scroll"
      offsetScrollbars
      scrollbarSize={4}
      style={{ maxWidth: '90vw', width: '100%', height: 60, overflowX: 'auto' }}
    >
      <Group spacing={0} noWrap my={10}>
        {(isSortVisible || (Array.isArray(sortSelected) && sortSelected.length > 0)) && (
          <>
            <FilterDropdownSortSelector
              filters={[...availableFilters, ...managerAdditionalSort]}
              sortSelected={sortSelected}
              onUpdateSort={onUpdateSort}
            />

            <Divider sx={{ height: '30px' }} ml={12} mr={12} orientation="vertical" />
          </>
        )}

        {Array.isArray(tagsList) && tagsList.length > 0 && (
          <FilterDropdownFieldSelector
            type="tags"
            filters={tagsList.filter((tag) => (Array.isArray(tagsSelected) ? !tagsSelected.includes(tag.name) : true))}
            onSelectFilter={(tag) => {
              if (typeof onSelectTag === "function") {
                onSelectTag(tag);
              }
            }}
            onClearSort={() => onSelectTag(null)}
            TargetComponent={({ onClick }) => (
              <Button
                variant="default"
                size="xs"
                mr={10}
                radius="md"
                leftIcon={<FontAwesomeIcon icon={solid("tag")} color="grey" size="sm" />}
                rightIcon={<FontAwesomeIcon icon={solid("chevron-down")} color="grey" size="sm" />}
                onClick={onClick}
              >
                <Text size="xs" color="grey" style={{ textTransform: "capitalize" }}>
                  {"Tag: "}{Array.isArray(tagsSelected) && tagsSelected.length > 0 ? tagsSelected.join(", ") : "None"}
                </Text>
              </Button>
            )}
          />
        )}

        {panelFilters.map((filter, index) => {
          const panelFilter = filtersSelected.find(f => (f.id === filter.id)) || {};
          return (
            <FilterDropdownValueSelector
              key={index}
              filter={{...filter, ...panelFilter}}
              optionCounts={allCounts}
              topLocations={topLocations}
              onUpdateFilter={(option_value, condition) => onUpdate(filter, option_value, condition)}
            />
          );
        })}

        {filtersSelected.map((filter, index) => {
          const isPanelFilter = panelFilters.find(f => (f.id === filter.id));
          if (isPanelFilter) return null;
          return (
            <FilterDropdownValueSelector
              key={index}
              filter={filter}
              optionCounts={allCounts}
              topLocations={topLocations}
              onUpdateFilter={(option_value, condition) => onUpdate(filter, option_value, condition)}
              onClearFilter={() => {
                if (typeof onUpdateFilter === "function") {
                  const newFilters = filtersSelected.filter(f => (f.id !== filter.id));
                  onUpdateFilter(newFilters);
                }
              }}
            />
          )
        })}

        <FilterDropdownFieldSelector
          filters={availableFilters}
          onSelectFilter={onSelectFilter}
          onPressCreateFilter={onPressCreateFilter}
        />

        {filtersSelected.length > 0 && (
          <>
            <Button
              variant="default"
              radius="md"
              size="xs"
              sx={{
                backdropFilter: 'blur(4px)',
                position: 'absolute',
                right: 0
              }}
              onClick={() => {
                if (typeof onUpdateFilter === "function") {
                  onUpdateFilter(null);
                }
                if (typeof onSelectTag === "function") {
                  onSelectTag(null);
                }
              }}
            >
              <Text size="xs" color="grey">Reset</Text>
            </Button>
            <Box style={{ width: 100 }}></Box>
          </>
        )}

      </Group>

    </ScrollArea>
  );
}